import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { MAIN_SITE_URL } from "@/mocks/site";
import { categories } from "@/mocks/categories";

const anchorLinks = [
  { id: "categories", key: "nav.categories", icon: "ri-apps-2-line" },
  { id: "qc", key: "nav.qc", icon: "ri-camera-line" },
  { id: "shipping", key: "nav.shipping", icon: "ri-truck-line" },
  { id: "review", key: "nav.review", icon: "ri-star-line" },
  { id: "safety", key: "nav.safety", icon: "ri-shield-check-line" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [showCategories, setShowCategories] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const light = !scrolled && location.pathname === "/";

  const scrollTo = (id: string) => {
    setOpen(false);
    if (location.pathname === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/", { state: { scrollTo: id } });
    }
  };

  const linkClass = (isLight: boolean) =>
    `whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold transition-colors cursor-pointer ${
      isLight
        ? "text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)] hover:bg-white/15"
        : "text-foreground-800 hover:bg-background-100"
    }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : light
            ? "bg-gradient-to-b from-black/60 via-black/25 to-transparent"
            : "bg-background-50 border-b border-background-200"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-white">
            <i className="ri-table-line text-lg w-5 h-5 flex items-center justify-center"></i>
          </span>
          <span
            className={`text-base font-bold ${
              light
                ? "text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]"
                : "text-foreground-950"
            }`}
          >
            {t("brand.name")}
          </span>
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          <Link to="/" className={linkClass(light)}>
            {t("nav.home")}
          </Link>
          <Link to="/guides/allchinabuy-spreadsheet-guide" className={linkClass(light)}>
            {t("nav.shoppingGuide")}
          </Link>
          {anchorLinks.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => scrollTo(l.id)}
              className={linkClass(light)}
            >
              <span className="flex items-center gap-1.5">
                <i className={`${l.icon} text-base w-4 h-4 flex items-center justify-center`}></i>
                {t(l.key)}
              </span>
            </button>
          ))}
        </div>

        <div className="hidden items-center gap-2 lg:flex">
          <LanguageSwitcher light={light} />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 cursor-pointer"
          >
            {t("nav.buyNow")}
          </a>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`flex h-10 w-10 items-center justify-center rounded-md lg:hidden cursor-pointer ${
            light ? "text-white" : "text-foreground-900"
          }`}
        >
          <i className={`text-2xl ${open ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </nav>

      {open && (
        <div className="max-h-[80vh] overflow-y-auto border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100"
            >
              <i className="ri-home-4-line text-base w-4 h-4 flex items-center justify-center"></i>
              {t("nav.home")}
            </Link>
            <Link
              to="/guides/allchinabuy-spreadsheet-guide"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100"
            >
              <i className="ri-shopping-bag-3-line text-base w-4 h-4 flex items-center justify-center"></i>
              {t("nav.shoppingGuide")}
            </Link>

            <button
              type="button"
              onClick={() => setShowCategories((v) => !v)}
              className="flex items-center justify-between rounded-md px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line text-base w-4 h-4 flex items-center justify-center"></i>
                {t("nav.categories")}
              </span>
              <i
                className={`ri-arrow-down-s-line text-base w-4 h-4 flex items-center justify-center transition-transform ${
                  showCategories ? "rotate-180" : ""
                }`}
              ></i>
            </button>

            {showCategories && (
              <div className="ml-3 grid grid-cols-2 gap-1 border-l border-background-200 pl-3">
                {categories.map((c) => (
                  <a
                    key={c.id}
                    href={c.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setOpen(false)}
                    className="truncate rounded-md px-2 py-2 text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-700"
                  >
                    {t(`categories.${c.id}`)}
                  </a>
                ))}
              </div>
            )}

            <div className="my-1 h-px bg-background-200"></div>

            {anchorLinks.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => scrollTo(l.id)}
                className="flex items-center gap-2 rounded-md px-3 py-2.5 text-left text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
              >
                <i className={`${l.icon} text-base w-4 h-4 flex items-center justify-center`}></i>
                {t(l.key)}
              </button>
            ))}

            <div className="mt-2 flex items-center justify-between gap-2 px-3 pb-2">
              <LanguageSwitcher />
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-600"
              >
                {t("nav.buyNow")}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}